import React, {memo, useState} from 'react'
import {KTSVG} from '../../../../_metronic/helpers'
import styles from './concessionariesSummaryCards.module.css'

type StatItem = {
  label: string
  count: number
  color: string
}

type CardId = 'monAut' | 'extract' | 'typeMonitor'

type StatCardProps = {
  id: CardId
  title: string
  items: StatItem[]
  mainIndex?: number
  isOpen: boolean
  isActive: boolean
  onToggle: (id: CardId) => void
}

const StatCard = memo(function StatCard({
  id,
  title,
  items,
  mainIndex = 0,
  isOpen,
  isActive,
  onToggle,
}: StatCardProps) {
  const mainItem = items[mainIndex]

  const bodyState = !isOpen ? 'closed' : isActive ? 'active' : 'inactive'

  return (
    <div className={`card card-flush h-100 ${styles.statCard} `}>
      <div
        className={`card-header border-0 px-4 py-3 ${styles.cardHeader} ${!isOpen || isActive ? 'bg-primary' : ''}`}
        style={{
          cursor: 'pointer',
          ...(isOpen && !isActive
            ? {backgroundColor: 'color-mix(in srgb, var(--bs-primary) 72%, black)'}
            : {}),
        }}
        onClick={() => onToggle(id)}
        role='button'
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onToggle(id)
          }
        }}
      >
        <div className='d-flex align-items-center justify-content-between w-100'>
          <div className='d-flex align-items-center gap-2'>
            <span className='card-label fw-bold fs-6 text-white mb-0'>{title}</span>
            <span
              style={{
                display: 'inline-flex',
                transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s ease',
              }}
            >
              <KTSVG
                path='/media/icons/duotune/arrows/arr072.svg'
                className='svg-icon-6 svg-icon-white'
              />
            </span>
          </div>
          {mainItem && (
            <span
              className={`fw-bolder fs-3 text-white ${styles.mainCount} ${isOpen ? styles.mainCountHidden : ''}`}
              aria-hidden={isOpen}
            >
              {mainItem.count}
            </span>
          )}
        </div>
      </div>

      <div
        className={`${styles.cardBodyWrap} ${
          bodyState === 'closed'
            ? styles.cardBodyWrapClosed
            : bodyState === 'active'
            ? styles.cardBodyWrapActive
            : styles.cardBodyWrapInactive
        }`}
      >
        <div
          className={`${styles.cardBodyInner} ${bodyState === 'inactive' ? styles.cardBodyInnerInactive : ''}`}
        >
          {isActive &&
            items.map((item, index) => (
              <div
                key={item.label}
                className={`d-flex align-items-center justify-content-between ${styles.statusRow} ${index < items.length - 1 ? 'mb-2' : ''}`}
              >
                <span className={`badge badge-light-${item.color} fs-8 fw-semibold`}>
                  {item.label}
                </span>
                <span className='fw-bold fs-5 text-gray-800'>{item.count}</span>
              </div>
            ))}
        </div>
      </div>
    </div>
  )
})

type Props = {
  stats: {
    statusMonAut: StatItem[]
    statusExtract: StatItem[]
    typeMonitor: StatItem[]
  }
}

export const ConcessionariesSummaryCards = memo(function ConcessionariesSummaryCards({
  stats,
}: Props) {
  const [isOpen, setIsOpen] = useState(false)
  const [activeCard, setActiveCard] = useState<CardId | null>(null)

  const handleToggle = (id: CardId) => {
    if (!isOpen) {
      setIsOpen(true)
      setActiveCard(id)
      return
    }

    if (activeCard === id) {
      setIsOpen(false)
      setActiveCard(null)
      return
    }

    setActiveCard(id)
  }

  return (
    <div className={styles.cardsRow}>
      <StatCard
        id='monAut'
        title='Monit. Automático'
        items={stats.statusMonAut}
        mainIndex={0}
        isOpen={isOpen}
        isActive={activeCard === 'monAut'}
        onToggle={handleToggle}
      />
      <StatCard
        id='extract'
        title='Extração'
        items={stats.statusExtract}
        mainIndex={0}
        isOpen={isOpen}
        isActive={activeCard === 'extract'}
        onToggle={handleToggle}
      />
      <StatCard
        id='typeMonitor'
        title='Tipo de Monitoramento'
        items={stats.typeMonitor}
        mainIndex={0}
        isOpen={isOpen}
        isActive={activeCard === 'typeMonitor'}
        onToggle={handleToggle}
      />
    </div>
  )
})

export type {StatItem}
